/**
 * Payee Payment Profiles — the bank account a payout is paid to. Only an effective VERIFIED
 * profile can be released for payment or sent to ACH. Numbers are encrypted; only last-4 shows.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, Landmark, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import {
  Card, FinanceOnly, PageHeader, StatusPill, TenantSelect, btnGhost, btnPrimary, btnSmall,
  errorText, inputCls, useLatestRequest, useTenantScope,
} from '../components/finance/financeUi';

const METHODS = [['manual_document', 'Voided cheque / bank letter'], ['manual_call', 'Verified by phone with bank'],
                 ['micro_deposit', 'Micro-deposit confirmed'], ['provider_verification', 'Provider verification']];

function AddProfile({ payee, scope, onDone }) {
  const [form, setForm] = useState({ account_number: '', routing_number: '', account_holder_name: payee.name, account_type: 'checking' });
  const valid = /^\d{9}$/.test(form.routing_number.trim()) && /^\d{4,17}$/.test(form.account_number.trim());
  const save = async () => {
    try {
      await api.request('/financials/payment-profiles', { method: 'POST',
        body: JSON.stringify({ ...scope.body, ...form, payee_user_id: payee.user_id, reason: 'Recorded by finance' }) });
      toast.success('Profile recorded — pending verification');
      onDone();
    } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <div className="grid gap-2 border-t bg-muted/30 px-5 py-4 sm:grid-cols-5" data-testid="add-profile-form">
      <input className={inputCls} placeholder="Account holder" value={form.account_holder_name}
             onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} data-testid="profile-holder-input" />
      <input className={inputCls} placeholder="Routing (9 digits)" value={form.routing_number} inputMode="numeric"
             onChange={(e) => setForm({ ...form, routing_number: e.target.value })} data-testid="profile-routing-input" />
      <input className={inputCls} placeholder="Account number" value={form.account_number} inputMode="numeric"
             onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="profile-account-input" />
      <select className={inputCls} value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}
              data-testid="profile-type-select">
        <option value="checking">Checking</option><option value="savings">Savings</option>
      </select>
      <div className="flex gap-2">
        <button type="button" className={btnPrimary} onClick={save} disabled={!valid} data-testid="profile-save-btn"
                title={valid ? '' : 'Routing must be 9 digits, account 4-17 digits'}>Save</button>
        <button type="button" className={btnGhost} onClick={onDone} data-testid="profile-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}

function Review({ payee, onDone }) {
  const [method, setMethod] = useState(METHODS[0][0]);
  const [reason, setReason] = useState('');
  const id = payee.profile.profile_id;
  const call = async (path, body, message) => {
    try {
      const result = await api.request(`/financials/payment-profiles/${id}/${path}`, { method: 'POST', body: JSON.stringify(body) });
      const released = result.released_payouts?.length;
      toast.success(released ? `${message} · ${released} held payout(s) released for payment` : message);
      onDone();
    } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-t bg-muted/30 px-5 py-4" data-testid="review-profile-form">
      <select className={`${inputCls} sm:w-64`} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="verify-method-select">
        {METHODS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button type="button" className={btnPrimary} onClick={() => call('verify', { verification_method: method }, 'Bank verified')}
              data-testid="verify-profile-btn"><BadgeCheck className="h-4 w-4" />Verify</button>
      <input className={`${inputCls} sm:w-64`} placeholder="Rejection reason" value={reason}
             onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
      <button type="button" className={btnGhost} disabled={!reason.trim()} onClick={() => call('reject', { reason }, 'Profile rejected')}
              data-testid="reject-profile-btn"><XCircle className="h-4 w-4" />Reject</button>
      <button type="button" className={btnGhost} onClick={onDone} data-testid="review-cancel-btn">Close</button>
    </div>
  );
}

export default function PaymentProfilesPage() {
  const { user } = useAuthStore();
  const scope = useTenantScope(user);
  const [payees, setPayees] = useState([]);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState('all');
  const nextRequest = useLatestRequest();
  const load = useCallback(async () => {
    if (!scope.ready) return;
    const isCurrent = nextRequest();
    try {
      const list = await api.request(`/financials/payment-profiles/payees?${scope.query}`);
      if (isCurrent()) { setPayees(list); setOpen({}); }
    } catch (err) { if (isCurrent()) { toast.error(errorText(err)); setPayees([]); } }
  }, [scope.ready, scope.query, nextRequest]);
  useEffect(() => { load(); }, [load]);
  if (!['super_admin', 'iso'].includes(user?.role)) return <FinanceOnly testid="payment-profiles-denied" />;
  const counts = payees.reduce((acc, p) => ({ ...acc, [p.payment_status]: (acc[p.payment_status] || 0) + 1 }), {});
  const shown = payees.filter((p) => filter === 'all' || (filter === 'blocked' ? p.payment_status !== 'VERIFIED' : p.payment_status === 'VERIFIED'));
  return (
    <div className="space-y-8" data-testid="payment-profiles-page">
      <PageHeader title="Payee Banking"
                  subtitle="The obligation stands either way — but no payout is released or sent to ACH until the payee has a verified bank account.">
        <TenantSelect scope={scope} testid="payment-profiles-tenant-select" />
      </PageHeader>
      <div className="flex flex-wrap gap-2" data-testid="payment-profile-filters">
        {[['all', `All (${payees.length})`], ['blocked', `Blocked (${payees.length - (counts.VERIFIED || 0)})`], ['verified', `Verified (${counts.VERIFIED || 0})`]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`profile-filter-${k}`}
                  className={`h-9 rounded-full px-4 text-sm transition-colors ${filter === k ? 'bg-primary text-primary-foreground' : 'border hover:bg-muted'}`}>{label}</button>
        ))}
      </div>
      <Card testid="payees-card" title="Payees" aside={<Landmark className="h-4 w-4 text-muted-foreground" />}>
        <ul className="divide-y">
          {shown.map((p) => (
            <li key={p.user_id} data-testid={`payee-row-${p.user_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.role?.replace(/_/g, ' ')} · {p.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  {p.profile && (
                    <span className="text-xs tabular-nums text-muted-foreground" data-testid={`payee-bank-${p.user_id}`}>
                      {p.profile.bank_display} · RTN {p.profile.routing_display}
                      {p.profile.verification_method ? ` · ${p.profile.verification_method.replace(/_/g, ' ')}` : ''}
                    </span>
                  )}
                  <StatusPill value={p.payment_status} testid={`payee-status-${p.user_id}`} />
                  {p.profile?.verification_status === 'PENDING' && (
                    <button type="button" className={btnSmall} onClick={() => setOpen({ [p.user_id]: 'review' })}
                            data-testid={`review-btn-${p.user_id}`}>Review</button>
                  )}
                  <button type="button" className={btnSmall} onClick={() => setOpen({ [p.user_id]: 'add' })}
                          data-testid={`add-profile-btn-${p.user_id}`}><Plus className="h-3.5 w-3.5" />{p.profile ? 'New account' : 'Add bank'}</button>
                </div>
              </div>
              {open[p.user_id] === 'add' && <AddProfile payee={p} scope={scope} onDone={load} />}
              {open[p.user_id] === 'review' && <Review payee={p} onDone={load} />}
            </li>
          ))}
          {!shown.length && <li className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="no-payees">No payees.</li>}
        </ul>
      </Card>
    </div>
  );
}
